import React, { forwardRef } from 'react'
import { useIntl } from 'react-intl'
import { Box } from '@mui/material'
import Logo from '../../../../assets/img/dostyq-online.png'
import { StudentRatingState } from '../../../../features/types'
import { ui } from '../../../../utils/uiTokens'
import { Level, LEVEL_COLORS, studentLevel } from '../ratingLevel'
import LevelCards from './LevelCards'
import { TotalScore } from './RatingTable'

interface Props {
    students: StudentRatingState[]
    subtitle: string
    average: number
    max: number
    counts: Record<Level, number>
    rated: number
}

const cell = { padding: '10px 12px', borderBottom: `1px solid ${ui.border}`, fontSize: '14px', color: ui.ink }
const head = { ...cell, fontSize: '12px', fontWeight: 600, color: ui.sub, textTransform: 'uppercase' as const, background: ui.surface }

// Картинка общего рейтинга для WhatsApp и печати: рисуется вне экрана и снимается html2canvas
const RatingPngSheet = forwardRef<HTMLDivElement, Props>(({ students, subtitle, average, max, counts, rated }, ref) => {
    const { formatMessage } = useIntl()

    return (
        <Box ref={ref} sx={{ width: 1100, p: 4, background: ui.card, color: ui.ink }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3 }}>
                <Box>
                    <img src={Logo} alt="Dostyq Online" style={{ width: 150 }} />
                    <Box sx={{ mt: 2, fontSize: '26px', fontWeight: 700 }}>{formatMessage({ id: 'overallRating' })}</Box>
                    <Box sx={{ mt: 0.5, fontSize: '14px', color: ui.pink, fontWeight: 600 }}>{subtitle}</Box>
                    <Box sx={{ mt: 2, fontSize: '14px', color: ui.sub }}>
                        {formatMessage({ id: 'groupAverage' })}:{' '}
                        <b style={{ fontSize: '20px', color: ui.ink }}>{average}</b> / {max}
                    </Box>
                </Box>
                <LevelCards counts={counts} total={rated} />
            </Box>

            <table style={{ width: '100%', marginTop: 24, borderCollapse: 'collapse', border: `1px solid ${ui.border}` }}>
                <thead>
                    <tr>
                        <th style={{ ...head, textAlign: 'left' }}>#</th>
                        <th style={{ ...head, textAlign: 'left' }}>{formatMessage({ id: 'student' })}</th>
                        <th style={head}>{formatMessage({ id: 'video' })}</th>
                        <th style={head}>{formatMessage({ id: 'test' })}</th>
                        <th style={head}>{formatMessage({ id: 'conspect' })}</th>
                        <th style={head}>{formatMessage({ id: 'zachet' })}</th>
                        <th style={head}>{formatMessage({ id: 'practice' })}</th>
                        <th style={head}>{formatMessage({ id: 'totalPoint' })}</th>
                    </tr>
                </thead>
                <tbody>
                    {students.map((s, i) => {
                        const level = studentLevel(s.totalPoint, s.maxPoint)
                        const c = level ? LEVEL_COLORS[level] : null
                        return (
                            <tr key={s.userSubjectEnrollId} style={{ background: c?.bg || ui.card }}>
                                <td style={{ ...cell, fontWeight: 600, borderLeft: `4px solid ${c?.accent || ui.card}` }}>
                                    {i + 1}
                                </td>
                                <td style={cell}>
                                    <div style={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</div>
                                    <div style={{ fontSize: '12px', color: ui.muted }}>{s.groupTitle}</div>
                                </td>
                                <td style={{ ...cell, textAlign: 'center', fontWeight: 600 }}>{s.videoPoint}</td>
                                <td style={{ ...cell, textAlign: 'center', fontWeight: 600 }}>{s.testPoint}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>{s.conspectPoint} / {s.conspectMax}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>{s.zachetPoint}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>{s.practicePoint}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>
                                    <TotalScore total={s.totalPoint} max={s.maxPoint} />
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: ui.muted }}>
                <span>{formatMessage({ id: 'studentsCount' }, { count: students.length })}</span>
                <span>{formatMessage({ id: 'ratingExportedAt' }, { date: new Date().toLocaleDateString('ru-RU') })}</span>
            </Box>
        </Box>
    )
})

export default RatingPngSheet
